// Static resume data derived from Nimisha's executive profile + LinkedIn +
// current role leading Wipro's AI Native practice. Displayed on the homepage.

export const PROFILE = {
  name: "Nimisha Mohan Srivastava",
  shortName: "Nimisha Mohan",
  currentRole: "Associate Director, AI Native Practice, Wipro",
  location: "Mumbai · Bangalore · Ann Arbor",
  email: "nimisham@umich.edu",
  linkedin: "https://www.linkedin.com/in/nimimohan",
  phone: "+91 99599 37117",
  headline:
    "Enterprise-scale value creation across strategy, operations and P&L.",
  summary:
    "Enterprise-scale value-creation executive in strategy and operations with 14+ years of global leadership across Fortune 500 and investor-backed ventures — holding full P&L, capital-allocation, and Board-level accountability. Led multi-region portfolios exceeding $20B in revenue, secured $96M+ in institutional capital, and delivered $120M+ in structural cost optimization alongside $25B+ in revenue expansion.",
  headlineMetrics: [
    { value: "$20B+", label: "Revenue portfolios led" },
    { value: "$96M+", label: "Institutional capital raised" },
    { value: "$120M+", label: "Structural cost optimization" },
    { value: "14+ yrs", label: "Global leadership" },
  ],
};

export const EXPERIENCE = [
  {
    company: "Wipro",
    role: "Associate Director — AI Native Practice",
    location: "Bangalore, India",
    dates: "2026 — Present",
    isCurrent: true,
    bullets: [
      "Define the multi-year roadmap for Wipro's AI Native practice — prioritising agentic AI, GenAI platforms and industry-specific accelerators across BFSI, Retail and Manufacturing, shaping a $50M+ pipeline of AI-led transformation programs.",
      "Lead solutions engineering for enterprise AI pursuits — architecting reusable reference solutions and proof-of-value frameworks that cut solution design cycles by ~40% and lifted win rates on strategic deals.",
      "Drive practice strategy and go-to-market — defining offerings, pricing constructs and partner alliances with hyperscalers and model providers to scale AI Native revenue across global accounts.",
      "Institutionalise an AI-first delivery operating model — governance, value-realisation metrics and talent upskilling that move clients from pilots to production at scale.",
    ],
    metrics: [
      "$50M+ AI pipeline shaped",
      "~40% faster solution design",
      "20+ enterprise AI pursuits",
      "Pilot → production at scale",
    ],
  },
  {
    company: "Amazon",
    role: "ROC Business Head (Supply Chain Manager)",
    location: "Hyderabad, India",
    dates: "Feb 2024 — May 2025",
    bullets: [
      "Owned operational and financial performance of a $130M global cost base across 544 NA/EU sites — directing org design, automation architecture and network optimization.",
      "Directed a 35-function, 535-employee global enterprise enabling capital-efficient scale across a 95,000-asset global network representing ~$1.43B in asset value.",
      "Re-architected enterprise vendor economics via AI-driven optimization across 5.2M+ annual repairs; embedded capital-disciplined performance governance across global teams.",
    ],
    metrics: [
      "+18.4% throughput",
      "+14.3% EBITDA contribution",
      "$21M+ labor expense avoided",
      "508K+ defects eliminated",
    ],
  },
  {
    company: "Reliance Jio (Reliance Industries)",
    role: "General Manager",
    location: "Mumbai, India",
    dates: "May 2023 — Feb 2024",
    bullets: [
      "Final decision authority for pricing and commercial strategy of the 4G Jio-Bharat device within the sub-₹1,000 segment.",
      "Institutionalised pan-India SME fulfillment across 1.5M+ annual orders and led nationwide digitisation across a $15B revenue portfolio.",
    ],
    metrics: [
      "$1B+ revenue preserved",
      "Gross margin 18% → 21%",
      "Working capital cycle 12 → 7 days",
      "+9.7% top-line growth",
    ],
  },
  {
    company: "Zoomcar",
    role: "Senior Director",
    location: "Bangalore, India",
    dates: "Apr 2021 — Apr 2023",
    bullets: [
      "Reported directly to the Board and led the 5-year corporate strategy for a $90M global mobility platform — final decision authority on strategy, capital allocation and operating-model design.",
      "Co-led a $92M institutional capital raise — shaping valuation thesis, long-range financial architecture, investor narrative and capital-deployment roadmap.",
      "Re-architected the marketplace to an asset-light model, exited underperforming geographies and institutionalised board-level OKR governance.",
    ],
    metrics: [
      "33% revenue CAGR defined",
      "+2,000 bps gross margin",
      "6,000 → 25,000 vehicles",
      "29% retention uplift, 55% CAC reduction",
    ],
  },
  {
    company: "Metadome.AI",
    role: "Chief of Staff — Strategy & Operations",
    location: "Bangalore, India",
    dates: "Nov 2020 — Apr 2021",
    bullets: [
      "Led pre-Series A capital raise — designing equity narrative, valuation framework and 3-year financial architecture.",
      "Secured ~$4M from Chiratae Ventures with institutional diligence supporting a targeted $1.4B valuation trajectory.",
    ],
    metrics: ["~$4M raised", "$1.4B valuation trajectory"],
  },
  {
    company: "Arvind Limited (via Omuni)",
    role: "Strategy & Operations Manager",
    location: "Bangalore, India",
    dates: "Feb 2020 — Oct 2020",
    bullets: [
      "Architected enterprise revenue-operations redesign — 75+ B2B KPIs and end-to-end governance overhaul.",
      "Led cross-functional product, growth, finance and risk teams (600+ employees across 120+ cities) to launch WhatsApp commerce.",
    ],
    metrics: [
      "+19% revenue growth",
      "+28% pipeline conversion",
      "+43% forecast accuracy",
      "1M orders via new channel",
    ],
  },
  {
    company: "Capital One",
    role: "Strategy Manager — Strategy & Analytics",
    location: "Richmond, Virginia, USA",
    dates: "Aug 2018 — Dec 2019",
    bullets: [
      "Led governance and capital-protection redesign across a $400M+ multi-business credit portfolio.",
      "Architected automation frameworks for financially vulnerable customer segments; led enterprise cost transformation across a 20M+ account portfolio.",
    ],
    metrics: [
      "-19% loss rate",
      "+300 bps risk-adjusted returns",
      "$50M+ recurring savings",
      "-27% compliance exceptions",
    ],
  },
  {
    company: "Wipro",
    role: "Lead Consultant — Leadership Development Program (STAR)",
    location: "Bangalore, India",
    dates: "Jul 2012 — Jul 2016",
    bullets: [
      "Architected enterprise-wide capital-allocation redesign for a $15B utility client.",
      "Designed 300+ enterprise software use-cases underpinning a $100M+ multi-year commercial roadmap across Telecom, BFSI and Utilities.",
    ],
    metrics: [
      "$1.5B capex reallocated",
      "+19% solution attach",
      "$9.5M recurring savings",
      "+120 bps ROIC",
    ],
  },
];

export const EDUCATION = [
  {
    school: "University of Michigan — Stephen M. Ross School of Business",
    degree: "MBA · Strategy, Finance & Operations",
    location: "Ann Arbor, MI",
    date: "Apr 2018",
    notes:
      "Ross Student Ambassador · Asia Business Conference (2017).",
  },
  {
    school: "National Institute of Technology, Warangal",
    degree: "B.Tech (Honors)",
    location: "Warangal, India",
    date: "May 2012",
    notes:
      "Founded the NIT Warangal chapter of the Indian Institute of Metals (188+ members). First prize, national paper presentation on Micro-Arc Oxidation (IIT Madras & NIT Warangal). Published research on cobalt-ferrite nanoparticle synthesis (ScienceDirect).",
  },
];

export const COMPETENCIES = [
  "Enterprise strategy",
  "P&L leadership",
  "Capital allocation",
  "International expansion",
  "Marketplace economics",
  "Pricing & margin architecture",
  "Fundraising & board engagement",
  "Digital & AI transformation",
  "Risk governance",
];
